/* CONTEXT:START
Component: JoeConfiguration
Status: build
Dependencies: Material-UI, mock data service, TestJoeDialog, JoeConfigurationHistory
Next: Task 1.3 - TestJoeDialog component
CONTEXT:END */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  Psychology as PsychologyIcon,
  History as HistoryIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { 
  JoeConfiguration as JoeConfigurationType,
  joeConfigurationApiService
} from '../../services/joeConfigurationApi';
import { PLACEHOLDER_INFO } from '../../services/mockJoeData';
import TestJoeDialog from './TestJoeDialog';
import JoeConfigurationHistory from './JoeConfigurationHistory';

// Props interface (following our established patterns)
export interface JoeConfigurationProps {
  // No props needed for Wave 1 - self-contained component
}

const JoeConfiguration: React.FC<JoeConfigurationProps> = () => {
  // State management
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [userPromptTemplate, setUserPromptTemplate] = useState<string>('');
  const [originalConfig, setOriginalConfig] = useState<JoeConfigurationType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isTestDialogOpen, setIsTestDialogOpen] = useState<boolean>(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState<boolean>(false);

  // Form validation state
  const [systemPromptError, setSystemPromptError] = useState<string>('');
  const [userPromptError, setUserPromptError] = useState<string>('');

  // Load active configuration on component mount
  useEffect(() => {
    loadActiveConfiguration();
  }, []);

  const loadActiveConfiguration = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const config = await joeConfigurationApiService.getActiveConfiguration();
      setOriginalConfig(config);
      setSystemPrompt(config.systemPrompt);
      setUserPromptTemplate(config.userPromptTemplate);
    } catch (err) {
      setError('Failed to load Joe configuration. Please try again.');
      console.error('Error loading configuration:', err);
    } finally {
      setLoading(false);
    }
  };

  // Form validation functions with enhanced feedback
  const validateSystemPrompt = (prompt: string): string => {
    const trimmed = prompt.trim();
    if (!trimmed) {
      return 'System prompt is required';
    }
    if (trimmed.length < 50) {
      return `System prompt must be at least 50 characters (currently ${trimmed.length})`;
    }
    if (trimmed.length > 3500) {
      return `System prompt must not exceed 3500 characters (currently ${trimmed.length})`;
    }
    return '';
  };

  const validateUserPromptTemplate = (template: string): string => {
    const trimmed = template.trim();
    if (!trimmed) {
      return 'User prompt template is required';
    }
    if (trimmed.length < 50) {
      return `User prompt template must be at least 50 characters (currently ${trimmed.length})`;
    }
    if (trimmed.length > 2500) {
      return `User prompt template must not exceed 2500 characters (currently ${trimmed.length})`;
    }
    if (!trimmed.includes('{{ENTRY_TYPE}}')) {
      return 'User prompt template must include {{ENTRY_TYPE}} placeholder';
    }
    if (!trimmed.includes('{{ENTRY_CONTENT}}')) {
      return 'User prompt template must include {{ENTRY_CONTENT}} placeholder';
    }
    return '';
  };

  // Handle input changes with validation
  const handleSystemPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSystemPrompt(value);
    setSystemPromptError(validateSystemPrompt(value));
    clearMessages();
  };

  const handleUserPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setUserPromptTemplate(value);
    setUserPromptError(validateUserPromptTemplate(value));
    clearMessages();
  };

  // Clear success/error messages
  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // Check if form is valid
  const isFormValid = (): boolean => {
    const systemError = validateSystemPrompt(systemPrompt);
    const userError = validateUserPromptTemplate(userPromptTemplate);
    return !systemError && !userError;
  };

  // Helper function to display user name with fallback
  const displayUserName = (config: JoeConfigurationType): string => {
    if (config.createdByName && config.createdByName.trim()) {
      return config.createdByName;
    }
    
    // Fallback logic for configurations without user names
    if (config.createdBy.includes('@')) {
      return config.createdBy; // Email address
    }
    
    // UUID fallback - show shortened version
    return `User ${config.createdBy.substring(0, 8)}...`;
  };

  // Check if form has changes
  const hasChanges = (): boolean => {
    if (!originalConfig) return false;
    return (
      systemPrompt !== originalConfig.systemPrompt ||
      userPromptTemplate !== originalConfig.userPromptTemplate
    );
  };

  // Handle save configuration
  const handleSave = async () => {
    if (!isFormValid()) {
      setSystemPromptError(validateSystemPrompt(systemPrompt));
      setUserPromptError(validateUserPromptTemplate(userPromptTemplate));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const newConfig = await joeConfigurationApiService.saveConfiguration({
        systemPrompt: systemPrompt.trim(),
        userPromptTemplate: userPromptTemplate.trim(),
        isActive: true,
        createdBy: 'admin@example.com', // Mock user ID
        description: 'Updated configuration via admin interface'
      });

      setOriginalConfig(newConfig);
      setSuccess('Configuration saved successfully! Joe will use the new prompts for all future evaluations.');
    } catch (err) {
      setError('Failed to save configuration. Please try again.');
      console.error('Error saving configuration:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle test Joe
  const handleTest = () => {
    if (!isFormValid()) {
      setSystemPromptError(validateSystemPrompt(systemPrompt));
      setUserPromptError(validateUserPromptTemplate(userPromptTemplate));
      return;
    }
    setIsTestDialogOpen(true);
  };

  // Handle view history
  const handleViewHistory = () => {
    setIsHistoryDialogOpen(true);
  };

  // Handle reset to original
  const handleReset = () => {
    if (originalConfig) {
      setSystemPrompt(originalConfig.systemPrompt);
      setUserPromptTemplate(originalConfig.userPromptTemplate);
      setSystemPromptError('');
      setUserPromptError('');
      clearMessages();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading Joe configuration...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Joe Configuration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Customize Joe's AI evaluation prompts to match your organization's standards and requirements.
        </Typography>
      </Box>

      {/* Success/Error Messages with enhanced styling */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }
          }} 
          onClose={() => setError(null)}
          action={
            <Button color="inherit" size="small" onClick={() => setError(null)}>
              Dismiss
            </Button>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>⚠️</span>
            {error}
          </Box>
        </Alert>
      )}
      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }
          }} 
          onClose={() => setSuccess(null)}
          action={
            <Button color="inherit" size="small" onClick={() => setSuccess(null)}>
              Dismiss
            </Button>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>✅</span>
            {success}
          </Box>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Configuration Panel with enhanced styling */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ 
            p: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'box-shadow 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
            }
          }}>
            {/* System Prompt Section with enhanced styling */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PsychologyIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" gutterBottom sx={{ mb: 0, fontWeight: 600 }}>
                  System Prompt
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                The system prompt defines Joe's role and evaluation approach. This sets the overall context for how Joe will assess submissions.
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={8}
                value={systemPrompt}
                onChange={handleSystemPromptChange}
                error={!!systemPromptError}
                helperText={
                  systemPromptError || (
                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: systemPrompt.length < 50 ? '#f57c00' : systemPrompt.length > 1800 ? '#f57c00' : '#4caf50' }}>
                        {systemPrompt.length}/3500 characters
                      </span>
                      {systemPrompt.length >= 50 && (
                        <span style={{ color: '#4caf50', fontSize: '0.75rem' }}>✓ Valid length</span>
                      )}
                    </span>
                  )
                }
                placeholder="Enter the system prompt that defines Joe's role and evaluation criteria..."
                variant="outlined"
                sx={{ 
                  mb: 2
                }}
              />
              
              {saving && <LinearProgress sx={{ mb: 2 }} />}
            </Box>

            <Divider sx={{ my: 3, borderColor: 'primary.light', opacity: 0.3 }} />

            {/* User Prompt Template Section with enhanced styling */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PsychologyIcon sx={{ mr: 1, color: 'secondary.main' }} />
                <Typography variant="h6" gutterBottom sx={{ mb: 0, fontWeight: 600 }}>
                  User Prompt Template
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                The user prompt template defines how Joe will receive and process individual submissions. Use placeholders to insert dynamic content.
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={6}
                value={userPromptTemplate}
                onChange={handleUserPromptChange}
                error={!!userPromptError}
                helperText={
                  userPromptError || (
                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: userPromptTemplate.length < 50 ? '#f57c00' : userPromptTemplate.length > 900 ? '#f57c00' : '#4caf50' }}>
                        {userPromptTemplate.length}/2500 characters
                      </span>
                      {userPromptTemplate.length >= 50 && userPromptTemplate.includes('{{ENTRY_TYPE}}') && userPromptTemplate.includes('{{ENTRY_CONTENT}}') && (
                        <span style={{ color: '#4caf50', fontSize: '0.75rem' }}>✓ Valid template</span>
                      )}
                    </span>
                  )
                }
                placeholder="Enter the user prompt template with placeholders..."
                variant="outlined"
                sx={{ 
                  mb: 2
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!isFormValid() || !hasChanges() || saving}
                sx={{ 
                  minWidth: 140,
                  position: 'relative',
                  '&.Mui-disabled': {
                    backgroundColor: saving ? 'primary.main' : undefined,
                    color: saving ? 'primary.contrastText' : undefined,
                    opacity: saving ? 0.8 : 0.6
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
                {saving && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      borderRadius: 1
                    }}
                  />
                )}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<PsychologyIcon />}
                onClick={handleTest}
                disabled={!isFormValid() || saving}
                sx={{ minWidth: 120 }}
              >
                Test Joe
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={handleViewHistory}
                disabled={saving}
                sx={{ minWidth: 120 }}
              >
                View History
              </Button>
              
              {hasChanges() && (
                <Button
                  variant="text"
                  onClick={handleReset}
                  disabled={saving}
                  color="secondary"
                >
                  Reset Changes
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Sidebar - Placeholder Information with enhanced styling */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ 
            background: 'linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)',
            border: '1px solid',
            borderColor: 'primary.light',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InfoIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Available Placeholders
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                Use these placeholders in your user prompt template. They will be automatically replaced with actual values when Joe evaluates submissions.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'success.main' }}>
                  Required Placeholders:
                </Typography>
                {PLACEHOLDER_INFO.required.map((placeholder, index) => (
                  <Box key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={placeholder.split(' - ')[0]}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        minWidth: 120
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                      {placeholder.split(' - ')[1]}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'info.main' }}>
                  Optional Placeholders:
                </Typography>
                {PLACEHOLDER_INFO.optional.map((placeholder, index) => (
                  <Box key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={placeholder.split(' - ')[0]}
                      size="small"
                      variant="outlined"
                      color="info"
                      sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '0.75rem',
                        minWidth: 120
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                      {placeholder.split(' - ')[1]}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ 
                fontStyle: 'italic',
                display: 'block',
                mt: 2,
                p: 1,
                backgroundColor: 'rgba(0,0,0,0.04)',
                borderRadius: 1,
                lineHeight: 1.4
              }}>
                💡 {PLACEHOLDER_INFO.description}
              </Typography>
            </CardContent>
          </Card>

          {/* Current Configuration Info with enhanced styling */}
          {originalConfig && (
            <Card sx={{ 
              mt: 2,
              background: 'linear-gradient(145deg, #e3f2fd 0%, #f3e5f5 100%)',
              border: '1px solid',
              borderColor: 'primary.light',
              borderRadius: 2
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h6" gutterBottom sx={{ mb: 0, fontWeight: 600 }}>
                    Current Configuration
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Version:
                    </Typography>
                    <Chip 
                      label={`v${originalConfig.version}`} 
                      size="small" 
                      color="primary" 
                      variant="filled"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Last Updated:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {new Date(originalConfig.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Updated By:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {displayUserName(originalConfig)}
                    </Typography>
                  </Box>
                  {originalConfig.description && (
                    <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        <strong>Description:</strong> {originalConfig.description}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* TODO: Add TestJoeDialog and JoeConfigurationHistory components in next tasks */}
      {/* TestJoeDialog will be implemented in Task 1.3 */}
      {/* JoeConfigurationHistory will be implemented in Task 1.4 */}
      
      {/* Test Joe Dialog */}
      <TestJoeDialog
        open={isTestDialogOpen}
        onClose={() => setIsTestDialogOpen(false)}
        systemPrompt={systemPrompt}
        userPromptTemplate={userPromptTemplate}
        onApplyConfig={async (testSystemPrompt: string, testUserPromptTemplate: string) => {
          // Update the form with the tested prompts
          setSystemPrompt(testSystemPrompt);
          setUserPromptTemplate(testUserPromptTemplate);
          
          // Save the configuration using existing save logic
          try {
            setSaving(true);
            setError(null);
            
            const newConfig = await joeConfigurationApiService.saveConfiguration({
              systemPrompt: testSystemPrompt.trim(),
              userPromptTemplate: testUserPromptTemplate.trim(),
              isActive: true,
              createdBy: 'admin@example.com', // Mock user ID
              description: 'Applied configuration after successful test'
            });

            setOriginalConfig(newConfig);
            setSuccess('Configuration applied successfully! Joe will use the new prompts for all future evaluations.');
            setIsTestDialogOpen(false); // Close dialog after successful save
          } catch (err) {
            // Error will be handled by TestJoeDialog
            throw err;
          } finally {
            setSaving(false);
          }
        }}
      />

      {/* Configuration History Dialog */}
      <JoeConfigurationHistory
        open={isHistoryDialogOpen}
        onClose={() => setIsHistoryDialogOpen(false)}
        onRevert={(configId) => {
          // Handle configuration revert
          setSuccess(`Successfully reverted to configuration ${configId}. Joe will now use the restored prompts.`);
          loadActiveConfiguration(); // Reload the active configuration
        }}
        currentActiveId={originalConfig?.id}
      />
    </Box>
  );
};

export default JoeConfiguration;
